'use strict';

const TRACKS = Object.freeze({
  dodge: Object.freeze({
    bpm: 112,
    bass: [110, 110, 130.81, 146.83, 98, 98, 130.81, 164.81],
    lead: [440, 523.25, 659.25, 523.25, 392, 493.88, 587.33, 493.88],
    chords: [[220, 261.63, 329.63], [196, 246.94, 293.66], [174.61, 220, 261.63], [196, 246.94, 329.63]],
    wave: 'triangle',
    leadWave: 'sine'
  }),
  boss: Object.freeze({
    bpm: 142,
    bass: [82.41, 82.41, 98, 110, 77.78, 92.5, 103.83, 73.42],
    lead: [329.63, 392, 466.16, 440, 311.13, 369.99, 440, 523.25],
    chords: [[164.81, 196, 246.94], [155.56, 185, 233.08], [146.83, 174.61, 220], [138.59, 174.61, 207.65]],
    wave: 'sawtooth',
    leadWave: 'square'
  })
});

class MusicSequencer {
  constructor(context, output) {
    this.context = context;
    this.output = output;
    this.track = null;
    this.trackGain = null;
    this.timer = null;
    this.nextStepAt = 0;
    this.step = 0;
    this.paused = false;
    this.activeNodes = new Set();
  }

  setTrack(name) {
    const next = TRACKS[name] ? name : null;
    if (next === this.track && !this.paused) return;
    this.stopLayer(0.55);
    this.track = next;
    this.step = 0;
    if (!next || this.paused || !this.context || this.context.state === 'closed') return;
    this.startLayer();
  }

  setPaused(paused) {
    const next = Boolean(paused);
    if (next === this.paused) return;
    this.paused = next;
    if (next) this.stopLayer(0.18, true);
    else if (this.track) this.startLayer();
  }

  startLayer() {
    if (!this.track || this.paused || this.timer || !this.output) return;
    const now = this.context.currentTime;
    const gain = this.context.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(1, now + 0.55);
    gain.connect(this.output);
    this.trackGain = gain;
    this.nextStepAt = now + 0.06;
    this.scheduleWindow();
    this.timer = setInterval(() => this.scheduleWindow(), 100);
  }

  stopLayer(fadeSeconds, preserveTrack) {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    const now = this.context && this.context.state !== 'closed' ? this.context.currentTime : 0;
    const gain = this.trackGain;
    if (gain && this.context && this.context.state !== 'closed') {
      try {
        gain.gain.cancelScheduledValues(now);
        gain.gain.setTargetAtTime(0.0001, now, Math.max(0.02, fadeSeconds / 4));
      } catch (error) { /* Audio is optional. */ }
      setTimeout(() => {
        try { gain.disconnect(); } catch (error) { /* no-op */ }
      }, Math.ceil((fadeSeconds + 0.15) * 1000));
    }
    this.trackGain = null;
    this.activeNodes.forEach((node) => {
      try { node.stop(now + Math.max(0.04, fadeSeconds)); } catch (error) { /* already stopped */ }
    });
    if (!preserveTrack && !this.track) this.step = 0;
  }

  scheduleWindow() {
    if (!this.track || this.paused || !this.trackGain || this.context.state === 'closed') return;
    const config = TRACKS[this.track];
    const stepDuration = 60 / config.bpm / 2;
    const horizon = this.context.currentTime + 0.7;
    while (this.nextStepAt < horizon) {
      this.scheduleStep(config, this.step, this.nextStepAt, stepDuration);
      this.nextStepAt += stepDuration;
      this.step = (this.step + 1) % 32;
    }
  }

  scheduleStep(config, step, time, duration) {
    const bassIndex = Math.floor(step / 2) % config.bass.length;
    if (step % 2 === 0) this.tone(config.bass[bassIndex], time, duration * 1.65, config.wave, 0.15);
    if (step % 4 === 0) this.kick(time, this.track === 'boss' ? 0.25 : 0.18);
    if (step % 8 === 4 || (this.track === 'boss' && step % 4 === 2)) this.hat(time, this.track === 'boss' ? 0.08 : 0.045);

    const leadIndex = step % config.lead.length;
    const playLead = this.track === 'boss' ? step % 2 === 1 : [1, 3, 6].includes(step % 8);
    if (playLead) this.tone(config.lead[leadIndex], time, duration * 0.72, config.leadWave, this.track === 'boss' ? 0.055 : 0.045);

    if (step % 8 === 0) {
      const chord = config.chords[Math.floor(step / 8) % config.chords.length];
      chord.forEach((frequency, index) => this.tone(frequency, time + index * 0.012, duration * 7.2, 'sine', 0.026));
    }
  }

  tone(frequency, time, duration, type, volume) {
    if (!this.trackGain) return;
    try {
      const oscillator = this.context.createOscillator();
      const gain = this.context.createGain();
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, time);
      gain.gain.setValueAtTime(0.0001, time);
      gain.gain.exponentialRampToValueAtTime(volume, time + 0.018);
      gain.gain.exponentialRampToValueAtTime(0.0001, time + Math.max(0.04, duration));
      oscillator.connect(gain);
      gain.connect(this.trackGain);
      oscillator.start(time);
      oscillator.stop(time + duration + 0.04);
      this.trackNode(oscillator);
    } catch (error) { /* Audio is optional. */ }
  }

  kick(time, volume) {
    if (!this.trackGain) return;
    try {
      const oscillator = this.context.createOscillator();
      const gain = this.context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(125, time);
      oscillator.frequency.exponentialRampToValueAtTime(42, time + 0.16);
      gain.gain.setValueAtTime(volume, time);
      gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.18);
      oscillator.connect(gain);
      gain.connect(this.trackGain);
      oscillator.start(time);
      oscillator.stop(time + 0.2);
      this.trackNode(oscillator);
    } catch (error) { /* Audio is optional. */ }
  }

  hat(time, volume) {
    this.tone(3200, time, 0.035, 'square', volume);
  }

  trackNode(node) {
    this.activeNodes.add(node);
    node.onended = () => this.activeNodes.delete(node);
  }

  destroy() {
    this.track = null;
    this.stopLayer(0.05);
    this.activeNodes.clear();
    this.output = null;
    this.context = null;
  }
}

module.exports = MusicSequencer;
